import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["slider", "prevBtn", "nextBtn", "dots"]

  connect() {
    this.currentIndex = 0
    this.touchStartX = 0
    this.touchEndX = 0
    this.isTransitioning = false

    this.updateSlider()
    this.updateButtons()
    this.updateDots()

    // Auto-resize sur changement de taille de fenêtre
    this.resizeObserver = new ResizeObserver(() => {
      this.updateSlider()
    })
    this.resizeObserver.observe(this.sliderTarget)
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  get itemsPerView() {
    const width = window.innerWidth
    if (width > 1024) return 4
    if (width > 768) return 3
    return 1
  }

  get maxIndex() {
    const cards = this.sliderTarget.children
    return Math.max(0, cards.length - this.itemsPerView)
  }

  prev() {
    if (this.isTransitioning || this.currentIndex === 0) return
    this.currentIndex--
    this.updateSlider()
    this.updateButtons()
    this.updateDots()
  }

  next() {
    if (this.isTransitioning || this.currentIndex >= this.maxIndex) return
    this.currentIndex++
    this.updateSlider()
    this.updateButtons()
    this.updateDots()
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    if (this.isTransitioning || index === this.currentIndex) return

    this.currentIndex = Math.min(index, this.maxIndex)
    this.updateSlider()
    this.updateButtons()
    this.updateDots()
  }

  updateSlider() {
    this.isTransitioning = true

    const cards = this.sliderTarget.children
    if (cards.length === 0) return

    const cardWidth = cards[0].offsetWidth

    // Read gap from CSS instead of hardcoding
    const style = window.getComputedStyle(this.sliderTarget)
    const gapValue = parseFloat(style.gap)
    const gap = isNaN(gapValue) ? 30 : gapValue

    const translateX = -(this.currentIndex * (cardWidth + gap))

    this.sliderTarget.style.transform = `translateX(${translateX}px)`

    // Reset transition flag après l'animation
    setTimeout(() => {
      this.isTransitioning = false
    }, 500)
  }

  updateButtons() {
    if (!this.hasPrevBtnTarget || !this.hasNextBtnTarget) return

    this.prevBtnTarget.disabled = this.currentIndex === 0
    this.nextBtnTarget.disabled = this.currentIndex >= this.maxIndex
  }

  updateDots() {
    if (!this.hasDotsTarget) return

    const dots = this.dotsTarget.querySelectorAll('.nouveautes-dot')
    dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('active')
      } else {
        dot.classList.remove('active')
      }
    })
  }

  // Touch/Swipe support pour mobile
  handleTouchStart(event) {
    if (this.isTransitioning) return
    this.touchStartX = event.touches[0].clientX
    this.currentTranslate = this.getCurrentTranslate()

    // Disable transition for direct follow
    this.sliderTarget.style.transition = 'none'
  }

  handleTouchMove(event) {
    if (!this.touchStartX) return

    const currentX = event.touches[0].clientX
    const diff = currentX - this.touchStartX
    const newTranslate = this.currentTranslate + diff

    this.sliderTarget.style.transform = `translateX(${newTranslate}px)`
  }

  handleTouchEnd(event) {
    if (!this.touchStartX) return

    const touchEndX = event.changedTouches[0].clientX
    const diff = touchEndX - this.touchStartX

    // Re-enable transition
    this.sliderTarget.style.transition = 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'

    const cards = this.sliderTarget.children
    if (cards.length === 0) return

    const cardWidth = cards[0].offsetWidth
    const style = window.getComputedStyle(this.sliderTarget)
    const gapValue = parseFloat(style.gap)
    const gap = isNaN(gapValue) ? 30 : gapValue
    const itemWidth = cardWidth + gap

    // Determine nearest index based on drag
    if (Math.abs(diff) > 50) {
      if (diff > 0 && this.currentIndex > 0) {
        this.currentIndex--
      } else if (diff < 0 && this.currentIndex < this.maxIndex) {
        this.currentIndex++
      }
    }

    this.updateSlider()
    this.updateButtons()
    this.updateDots()

    this.touchStartX = 0
  }

  getCurrentTranslate() {
    const style = window.getComputedStyle(this.sliderTarget)
    const matrix = new WebKitCSSMatrix(style.transform)
    return matrix.m41
  }
}